/* eslint-disable */
import { HttpClient } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { Ability, AbilityBuilder } from "@casl/ability";
import { ELocalStorage } from "@core/enums/LocalStorage.enum";
import {
  IPermission,
  IPermissions,
} from "@core/interfaces/permissions.interface";
import { LocalService } from "@core/services/local-storage.service";
import { FormModalService } from "@core/services/modal.service";
import { environment } from "@environments/environment";
import { jwtDecode } from "jwt-decode";
import { concatMap, Observable, of } from "rxjs";
import { catchError, finalize, map, switchMap, tap } from "rxjs/operators";
import {
  IJwtToken,
  ILoginResponse,
  IOneIDState,
  IUserData,
} from "../models/auth.model";
import { DistrictRegionChangeFormService } from "@metronic/layout/core/components/district-region-change-form/district-region-change-form.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  apiUrl = `${environment.apiUrl}user-service`;
  isLoading = signal<boolean>(false);
  isOneIdLoading = signal<boolean>(false);
  readonly userData = signal<IUserData | undefined>({
    id: 0,
    username: "admin",
    firstName: "Admin",
    lastName: "Admin",
    companyId: 0,
    companies: [],
    companyIds: [],
    role: {
      id: 1,
      name: "ADMIN",
      level: "string",
      prefix: "string",
    },
  });

  constructor(
    private _localStorageService: LocalService,
    private _router: Router,
    private _http: HttpClient,
    private _ability: Ability,
    private _modalService: FormModalService,
    private _districtRegionChangeFormService: DistrictRegionChangeFormService
  ) {
    const user = _localStorageService.getData("user");
    if (user !== null) {
      this.userData.set(user);
    }
  }

  loginByOneID(code: string): Observable<IPermissions> {
    this.isLoading.set(true);
    return this._http
      .post<ILoginResponse>(`${this.apiUrl}/one-id/login`, { code })
      .pipe(
        map((auth: ILoginResponse) =>
          this.setAuthFromLocalStorage(auth as ILoginResponse)
        ),
        concatMap(() => this.getUserDataById()),
        concatMap(() => this.getBlockedMenuCompany()),
        concatMap(() => this.getUserPermissions()),
        finalize(() => this.isLoading.set(false))
      );
  }

  login(data: {
    username: string;
    password: string;
  }): Observable<IPermissions> {
    this.isLoading.set(true);
    return this._http
      .post<ILoginResponse>(`${this.apiUrl}/users/login`, data)
      .pipe(
        map((auth: ILoginResponse) =>
          this.setAuthFromLocalStorage(auth as ILoginResponse)
        ),
        concatMap(() => this.getUserDataById()),
        concatMap(() => this.getBlockedMenuCompany()),
        concatMap(() => this.getUserPermissions()),
        finalize(() => this.isLoading.set(false))
      );
  }

  logout() {
    this.userData.set(undefined);
    this.successLogout();
    return this._http
      .post(`${this.apiUrl}/users/logout`, {
        refresh_token: this._localStorageService.getData(
          ELocalStorage.REFRESH_TOKEN
        ),
      })
      .pipe(
        catchError((err) => {
          console.error(err);
          return of({});
        }),
        finalize(() => {
          this.successLogout();
        })
      );
  }

  getUserDataById(): Observable<IUserData> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.access_token) {
      return of({
        id: 0,
        username: "admin",
        firstName: "Admin",
        lastName: "Admin",
        companyId: 0,
        companyIds: null,
        companies: null,
        role: {
          id: 1,
          name: "ADMIN",
          level: "string",
          prefix: "string",
        },
      });
    }

    this.isLoading.set(true);
    return this._http
      .get<IUserData>(`${this.apiUrl}/users/${auth.user_id}`)
      .pipe(
        map((user: IUserData) => {
          if (user) {
            this.isLoading.set(false);
            this.userData.set(user);
            this._localStorageService.setData(
              ELocalStorage.USER,
              JSON.stringify(user)
            );
            this._localStorageService.setData(
              ELocalStorage.ROLE,
              JSON.stringify(user.role.name)
            );
            this._localStorageService.setData(
              ELocalStorage.LEVEL,
              JSON.stringify(user.role.level)
            );
            this._localStorageService.setData(
              ELocalStorage.COMPANY_ID,
              JSON.stringify(user.companyId)
            );
            this._localStorageService.setData(
              ELocalStorage.REGION_ID,
              JSON.stringify(user.regionId)
            );
            this._localStorageService.setData(
              ELocalStorage.DISTRICT_ID,
              JSON.stringify(user.districtId)
            );
          } else {
            this.successLogout();
          }
          return user;
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  getUserPermissions(): Observable<IPermissions> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.access_token) {
      return of({ id: 1, name: "USER", permissions: [] });
    }

    this.isLoading.set(true);
    return this._http
      .get<IPermissions>(`${this.apiUrl}/roles/${auth.role}/permissions`)
      .pipe(
        map((permList: IPermissions) => {
          if (permList) {
            this.updateAbility(permList?.permissions);
            this._localStorageService.setData(
              ELocalStorage.PERMISSIONS,
              JSON.stringify(permList?.permissions)
            );
          } else {
            this.successLogout();
          }
          return permList;
        }),
        catchError((err) => {
          console.error(err);
          this.successLogout();
          return of({
            id: 1,
            name: "TEST",
            permissions: [{ id: 1, name: "TEST", description: "Test" }],
          });
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  getBlockedMenuCompany() {
    if (
      this.userData()?.companyId &&
      (this.userData()?.role.name == "COMPANY_ADMIN" ||
        this.userData()?.role.name == "OPERATOR" ||
        this.userData()?.role.name == "INSPECTOR")
    ) {
      return this._http
        .get(
          `${this.apiUrl}/company/${this.userData()?.companyId}/blocked-menus`
        )
        .pipe(
          catchError((err) => {
            console.error(err);
            return of([]);
          }),
          map((res: any) => res?.menuItems.map((item: any) => item.itemId)),
          tap((res) =>
            this._localStorageService.setData(
              ELocalStorage.BLOCEKD_MENUS,
              JSON.stringify(res)
            )
          )
        );
    }
    return of([]);
  }

  updateAccessToken(): Observable<IPermissions> {
    return this._http
      .post<ILoginResponse>(`${this.apiUrl}/users/refresh-token`, {
        refreshToken: this._localStorageService.getData(
          ELocalStorage.REFRESH_TOKEN
        ),
      })
      .pipe(
        map((response: ILoginResponse) => {
          if (response && response.access_token) {
            this.setAuthFromLocalStorage(response);
          } else {
            this.successLogout();
          }
          return response;
        }),
        concatMap(() => this.getBlockedMenuCompany()),
        switchMap(() => this.getUserPermissions()),
        catchError((err) => {
          console.error("err", err);
          return of({ id: 1, name: "USER", permissions: [] });
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  public successLogout() {
    this._router
      .navigate(["/auth"], { queryParamsHandling: "merge" })
      .then(() => {
        this.clearLocalStorage();
        this._ability.update([]);
        this._modalService.closeAll();
        this._districtRegionChangeFormService.filterForm.reset();
        this.userData.set(undefined);
      });
  }

  private setAuthFromLocalStorage(auth: ILoginResponse): boolean {
    if (auth && auth.access_token) {
      const decodedToken: IJwtToken = jwtDecode(auth.access_token);
      const { access_token, refresh_token, expires_in, refresh_expires_in } =
        auth;
      this._localStorageService.setData(
        ELocalStorage.USER_ID,
        decodedToken.user_id.toString() || "0"
      );
      this._localStorageService.setData(
        ELocalStorage.ROLE,
        decodedToken.realm_access.roles[0].includes("default")
          ? decodedToken.realm_access.roles[1]
          : decodedToken.realm_access.roles[0]
      );
      this._localStorageService.setData(
        ELocalStorage.ACCESS_TOKEN,
        access_token
      );
      this._localStorageService.setData(
        ELocalStorage.REFRESH_TOKEN,
        refresh_token
      );
      this._localStorageService.setData(
        ELocalStorage.EXPIRES_IN,
        JSON.stringify(expires_in)
      );
      this._localStorageService.setData(
        ELocalStorage.REFRESH_EXPIRES_IN,
        JSON.stringify(refresh_expires_in)
      );
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage():
    | (ILoginResponse & { user_id: number; role: string })
    | undefined {
    try {
      const access_token = this._localStorageService.getData(
        ELocalStorage.ACCESS_TOKEN
      );
      const refresh_token = this._localStorageService.getData(
        ELocalStorage.REFRESH_TOKEN
      )!;
      const expires_in = +this._localStorageService.getData(
        ELocalStorage.EXPIRES_IN
      )!;
      const refresh_expires_in = +this._localStorageService.getData(
        ELocalStorage.REFRESH_EXPIRES_IN
      )!;
      const firstName = this._localStorageService.getData(
        ELocalStorage.FIRSTNAME
      )!;
      const lastName = this._localStorageService.getData(
        ELocalStorage.REFRESH_EXPIRES_IN
      )!;
      const username = this._localStorageService.getData(
        ELocalStorage.USERNAME
      )!;
      const role = this._localStorageService.getData(ELocalStorage.ROLE)!;
      const user_id = +this._localStorageService.getData(
        ELocalStorage.USER_ID
      )!;

      if (!access_token) {
        return undefined;
      }

      return {
        access_token,
        refresh_token,
        expires_in,
        refresh_expires_in,
        firstName,
        lastName,
        username,
        role,
        user_id,
        status: true,
      };
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  updateAbility(permissions: IPermission[]) {
    const { can, rules } = new AbilityBuilder(Ability);

    if (permissions.length === 0) {
      this.successLogout();
      return;
    }

    permissions.forEach((permission: IPermission) => {
      const [subject, action] = permission.name.split(":");
      can(action, subject);
    });

    this._ability.update(rules);
  }

  private clearLocalStorage() {
    const oldKeys = ["kt_theme_mode_value", "language", "layout"];
    const oldValues = oldKeys.map((item: string) => localStorage.getItem(item));
    localStorage.clear();
    oldValues.forEach((item: any, index: number) => {
      if (item) {
        localStorage.setItem(oldKeys[index], item);
      }
    });
  }

  oneIdSignIn() {
    this.isOneIdLoading.set(true);

    this._http.get<IOneIDState>(`${this.apiUrl}/one-id/state`).subscribe({
      next: (oneIdParams) => {
        let oneIdSignInUrl = oneIdParams.oneIdUrl;
        oneIdSignInUrl += `?response_type=${oneIdParams.responseType}&`;
        oneIdSignInUrl += `client_id=${oneIdParams.clientId}&`;
        oneIdSignInUrl += `redirect_uri=${location.origin}/auth&`;
        oneIdSignInUrl += `scope=${oneIdParams.scope}&`;
        oneIdSignInUrl += `state=${oneIdParams.state}`;
        location.href = oneIdSignInUrl;
      },
      error: (err) => {
        console.log(err);
        this.isOneIdLoading.set(false);
      },
    });
  }

  isCompanyEmployee(): boolean {
    return (
      this.userData()?.companyId !== null ||
      typeof this.userData()?.companyId === "number"
    );
  }
}
